import React, { useMemo } from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PurchaseCardCollectedByUserFragmentDoc } from '@graphql/reactQuery';
import { getSingularOrPlural, preciseMillify } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PurchaseCardCollectedByUser on User {
    id
    username
    publicAddress
    webappUri
    avatar {
      id
      url
    }
  }
`);

const AVATAR_SIZE = 24;

export const CollectedBy = ({
  collectedByFrag,
  totalMinted,
  releaseId,
}: {
  collectedByFrag: FragmentType<PurchaseCardCollectedByUserFragmentDoc>[];
  totalMinted: number;
  releaseId: string;
}) => {
  const { openModal } = useModal();
  const collectedBy = getFragment(PurchaseCardCollectedByUserFragmentDoc, collectedByFrag);

  const noItemsToShow = useMemo(() => {
    return <span>Be the first to mint</span>;
  }, []);

  const onMintCountClick = () => {
    openModal(ModalType.RELEASE_MINTS, { body: <ReleaseMintsModal releaseId={releaseId} /> });
  };

  return (
    <div
      className={clsx(
        'flex h-full w-full items-center px-3',
        collectedBy.length > 0 && 'gap-[10px]',
      )}
    >
      <Avatars showNewSongCard>
        {collectedBy
          ?.slice(0, 3)
          .reverse()
          .map(user => {
            return (
              <AvatarContainer key={user.id}>
                <Avatar
                  src={user.avatar?.url}
                  size={AVATAR_SIZE}
                  username={user.username}
                  walletAddress={user.publicAddress}
                  hasShadow
                  hasBorder={false}
                  hasDarkBorder
                  borderRadius="50%"
                  webappUri={user.webappUri}
                />
              </AvatarContainer>
            );
          })}
      </Avatars>
      <div className="flex w-full flex-wrap font-base text-base-xs text-base200 hover:text-white md:text-base-xs">
        {collectedBy.length <= 0 ? (
          noItemsToShow
        ) : (
          <button onClick={onMintCountClick}>
            {preciseMillify({
              value: totalMinted,
              precision: 5,
            })}{' '}
            {getSingularOrPlural({
              count: totalMinted,
              word: 'mint',
            })}
          </button>
        )}
      </div>
    </div>
  );
};

const AvatarContainer = styled('div', {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
});

const Avatars = styled('div', {
  display: 'flex',
  flexDirection: 'row-reverse',

  //second and third child
  [`& ${AvatarContainer}:nth-child(2), & ${AvatarContainer}:nth-child(3)`]: {
    marginRight: -18,
  },

  variants: {
    showNewSongCard: {
      true: {
        [`& ${AvatarContainer}:nth-child(2), & ${AvatarContainer}:nth-child(3)`]: {
          marginRight: -12,
        },
      },
    },
  },
});
